import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SHARED_COPY } from './strings';

// Focus-mode header for the V2 booking flow: a slim, translucent sticky bar
// holding a single "go back" affordance that exits the flow to the movie page.
// Ported from the redesign prototype (seats-app.jsx FocusHeader/BackControl),
// reduced to the back control only (no eyebrow, destination label, or logo).
export function BookingFlowHeader({ backHref }: { backHref?: string | undefined }) {
  const href = backHref ?? '/';
  const [hover, setHover] = useState(false);

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 40,
        background: 'color-mix(in srgb, var(--color-surface) 82%, transparent)',
        backdropFilter: 'saturate(140%) blur(10px)',
        WebkitBackdropFilter: 'saturate(140%) blur(10px)',
        borderBottom: '1px solid color-mix(in srgb, var(--color-border) 70%, transparent)',
      }}
    >
      <div className="mx-auto flex h-[60px] w-full max-w-6xl items-center px-4 lg:h-[66px] lg:px-16">
        <a
          href={href}
          aria-label={SHARED_COPY.back}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          className="inline-flex select-none items-center"
          style={{ gap: 13, textDecoration: 'none' }}
        >
          <span
            className="grid shrink-0 place-items-center"
            style={{
              width: 42,
              height: 42,
              borderRadius: 999,
              background: hover ? 'var(--color-brand)' : 'var(--color-surface)',
              color: hover ? '#fff' : 'var(--color-fg)',
              border: `1px solid ${hover ? 'var(--color-brand)' : 'var(--color-border)'}`,
              boxShadow: hover ? '0 8px 22px rgba(20,18,12,.16)' : '0 1px 2px rgba(20,18,12,.05)',
              transition: 'background .24s, color .24s, border-color .24s, box-shadow .24s, transform .24s',
              transform: hover ? 'translateX(-3px)' : 'none',
            }}
          >
            <svg
              width="19"
              height="19"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.1"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 19l-7-7 7-7" />
              <path
                d="M19 12H5"
                style={{
                  strokeDasharray: 14,
                  strokeDashoffset: hover ? 0 : 8,
                  transition: 'stroke-dashoffset .26s ease',
                }}
              />
            </svg>
          </span>
          <span
            className="text-[14.5px] lg:text-[15.5px]"
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              letterSpacing: '-.012em',
              color: hover ? 'var(--color-brand)' : 'var(--color-fg)',
              transition: 'color .24s',
            }}
          >
            {SHARED_COPY.back}
          </span>
        </a>
      </div>
    </header>
  );
}

// Renders the focus-mode header only on booking-flow steps that DON'T supply
// their own in-page back-nav. The Red Carpet refresh gives seats (MVK-2388),
// concessions (MVK-2390) and checkout (MVK-2395) their own thin back-nav, so the
// global header is hidden there to avoid a duplicate "back" affordance. Must be
// rendered inside a Router.
export function BookingFlowHeaderSlot({ backHref }: { backHref?: string | undefined }) {
  const { pathname } = useLocation();
  const hasOwnBackNav =
    pathname === '/' ||
    pathname === '/seats' ||
    pathname === '/concessions' ||
    pathname === '/checkout';
  if (hasOwnBackNav) return null;
  return <BookingFlowHeader backHref={backHref} />;
}
